import React from "react";
import EnhancedLatexRenderer from "./EnhancedLatexRenderer";
import { AttributeVisualization } from "./AttributeVisualization";

// ============================================================================
// CONSTANTS
// ============================================================================

const SOURCE_KEYWORDS = [
    "Reference source:",
    "Source Link:",
    "Source Links:",
    "Source links:",
    "Source:",
    "Sources:",
    "資料來源：",
    "來源：",
    "來源:",
    "來源連結：",
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Extract action links from text based on source keywords
 * @param {string} text - The message text to parse
 * @returns {Array<{title: string, url: string}>} - Array of extracted links
 */
export function extractActionLinks(text) {
    const links = [];

    // Find the source section (bounded to its own block — see
    // findSourceSection below for why it can't run to the end of the string).
    const sourceContent = findSourceSection(text);
    if (!sourceContent) return links;

    // Strip markdown link syntax [label](url) → keep only the href URL.
    // Handles both [text](url) and [url](url) forms the AI may produce.
    const stripped = sourceContent.replace(/\[([^\]]*)\]\(([^)]+)\)/g, "$2");

    // Split by "https" to get individual URLs
    const urlParts = stripped.split(/(?=https)/);

    urlParts.forEach((part) => {
        const trimmedPart = part.trim();
        if (!trimmedPart.startsWith("https")) return;

        // Stop at any character that can't be part of a URL (markdown/whitespace)
        const rawUrl = trimmedPart.split(/[\s\])[>,"'`]/)[0];
        if (!rawUrl) return;

        const url = rawUrl.replace(
            "storage.cloud.google.com",
            "storage.googleapis.com",
        );

        const title = extractTitleFromUrl(url);
        links.push({ title, url });
    });

    return links;
}

/**
 * Find the { start, end } span of a source section within text: from a
 * source keyword to the next blank line (or end of string), whichever comes
 * first. Returns null if no keyword is found.
 *
 * A message can be several concatenated blocks (spec §3.3: a table block
 * followed by an analysis block, joined with "\n\n"). The table block ends
 * with its own "Source:" line — that line is only one block's worth of
 * sources, not necessarily the end of the whole message. Bounding the match
 * to the next blank line keeps it from swallowing any block appended after.
 */
function findSourceSectionSpan(text, keywords) {
    let bestIndex = -1;
    let bestKeywordLength = 0;
    for (const keyword of keywords) {
        const index = text.indexOf(keyword);
        if (index !== -1 && (bestIndex === -1 || index < bestIndex)) {
            bestIndex = index;
            bestKeywordLength = keyword.length;
        }
    }
    if (bestIndex === -1) return null;

    const contentStart = bestIndex + bestKeywordLength;
    const blankLineMatch = text.slice(contentStart).match(/\n\s*\n/);
    const contentEnd = blankLineMatch
        ? contentStart + blankLineMatch.index
        : text.length;

    return { start: bestIndex, end: contentEnd };
}

function findSourceSection(text) {
    const span = findSourceSectionSpan(text, SOURCE_KEYWORDS);
    return span ? text.slice(span.start, span.end) : null;
}

/**
 * Extract a readable title from a URL
 */
function extractTitleFromUrl(url) {
    // Strip the query string and fragment before looking at the path. GPT cites
    // product pages as `.../tg-asd35ab-en/?utm_source=openai` — a trailing slash
    // plus a query — so taking the last path segment first left every link
    // named "?Utm Source=Openai".
    const withoutQuery = url.split("#")[0].split("?")[0];
    const pathParts = withoutQuery.split("/").filter(Boolean);
    let filename = pathParts[pathParts.length - 1] ?? "";

    // Convert to readable title
    const title = filename
        .replace(/\.pdf$/i, "")
        .replace(/[-_]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (l) => l.toUpperCase());

    return title || "Document";
}

/**
 * Hosts known to refuse being framed (X-Frame-Options / frame-ancestors), so a
 * side panel would only ever show a blank box. Add to this list when another
 * source turns out to block embedding; everything else keeps the panel.
 */
const FRAME_BLOCKED_HOSTS = ["tglobalcorp.com"];

function isFrameBlocked(url) {
    if (typeof url !== "string") return false;
    try {
        const { hostname } = new URL(url);
        return FRAME_BLOCKED_HOSTS.some(
            (h) => hostname === h || hostname.endsWith(`.${h}`),
        );
    } catch {
        return false;
    }
}

/**
 * Clean message text by removing the source link section.
 *
 * Only the source section itself is cut (keyword to next blank line) — see
 * findSourceSectionSpan above. Cutting to the end of the string would drop
 * any block appended after the table's inline "Source:" line (spec §3.3's
 * two-block table+analysis messages).
 */
export function cleanMessageText(text) {
    const span = findSourceSectionSpan(text, SOURCE_KEYWORDS);
    if (!span) return text.trim();
    return (text.slice(0, span.start) + text.slice(span.end)).trim();
}

/**
 * Parse JSON from text with proper brace matching
 * @returns {{data: object|null, endIndex: number}}
 */
function parseJsonFromText(text) {
    const jsonStart = text.indexOf("{");
    if (jsonStart === -1) return { data: null, endIndex: 0 };

    let braceCount = 0;
    let jsonEnd = -1;

    for (let i = jsonStart; i < text.length; i++) {
        if (text[i] === "{") braceCount++;
        if (text[i] === "}") {
            braceCount--;
            if (braceCount === 0) {
                jsonEnd = i + 1;
                break;
            }
        }
    }

    if (jsonEnd === -1) return { data: null, endIndex: 0 };

    try {
        const jsonStr = text.substring(jsonStart, jsonEnd);
        const data = JSON.parse(jsonStr);
        return { data, endIndex: jsonEnd };
    } catch (e) {
        console.error("Failed to parse JSON:", e);
        return { data: null, endIndex: 0 };
    }
}

/**
 * Extract table content from text after "table:" keyword
 * @returns {{tableContent: string, afterTable: string}}
 */
function extractTableContent(textAfterKeyword) {
    // Try to find table end with double newline
    const tableEndMatch = textAfterKeyword.match(/\n\n/);

    if (tableEndMatch) {
        return {
            tableContent: textAfterKeyword
                .substring(0, tableEndMatch.index)
                .trim(),
            afterTable: textAfterKeyword.substring(tableEndMatch.index).trim(),
        };
    }

    // Find last line with pipe character (table row)
    const lines = textAfterKeyword.trim().split("\n");
    let lastTableLineIndex = -1;

    for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].includes("|")) {
            lastTableLineIndex = i;
            break;
        }
    }

    if (lastTableLineIndex !== -1) {
        return {
            tableContent: lines
                .slice(0, lastTableLineIndex + 1)
                .join("\n")
                .trim(),
            afterTable: lines
                .slice(lastTableLineIndex + 1)
                .join("\n")
                .trim(),
        };
    }

    return {
        tableContent: textAfterKeyword.trim(),
        afterTable: "",
    };
}

// ============================================================================
// CONTENT PARSER - Converts text to structured content blocks
// ============================================================================

/**
 * Content block types
 */
const ContentType = {
    TEXT: "text",
    TABLE: "table",
    VIZ: "viz",
};

/**
 * Parse message text into structured content blocks
 * @param {string} text - The cleaned message text
 * @returns {Array<{type: string, content: any}>}
 */
function parseContentBlocks(text) {
    const blocks = [];
    let remaining = text;

    while (remaining.length > 0) {
        const vizIndex = remaining.toLowerCase().indexOf("viz:");
        const tableIndex = remaining.toLowerCase().indexOf("table:");

        // No special content found - add remaining as text
        if (vizIndex === -1 && tableIndex === -1) {
            if (remaining.trim()) {
                blocks.push({
                    type: ContentType.TEXT,
                    content: remaining.trim(),
                });
            }
            break;
        }

        // Determine which comes first
        const nextSpecialIndex = getFirstValidIndex(vizIndex, tableIndex);
        const isVizNext = nextSpecialIndex === vizIndex && vizIndex !== -1;

        // Add text before the special content
        const beforeText = remaining.substring(0, nextSpecialIndex).trim();
        if (beforeText) {
            blocks.push({ type: ContentType.TEXT, content: beforeText });
        }

        // Parse the special content
        if (isVizNext) {
            const afterKeyword = remaining.substring(vizIndex + 4);
            const { data, endIndex } = parseJsonFromText(afterKeyword);

            if (data) {
                blocks.push({ type: ContentType.VIZ, content: data });
                remaining = afterKeyword.substring(endIndex).trim();
            } else {
                // Failed to parse viz, treat as text and continue
                remaining = afterKeyword.trim();
            }
        } else {
            const afterKeyword = remaining.substring(tableIndex + 6);
            const { tableContent, afterTable } =
                extractTableContent(afterKeyword);

            if (tableContent) {
                blocks.push({ type: ContentType.TABLE, content: tableContent });
            }
            remaining = afterTable;
        }
    }

    return blocks;
}

/**
 * Get the first valid (non-negative) index, or the smaller of two valid indices
 */
function getFirstValidIndex(a, b) {
    if (a === -1) return b;
    if (b === -1) return a;
    return Math.min(a, b);
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Returns true when the viewport is >= 768px (Tailwind's "md" breakpoint).
 * Updates reactively on window resize.
 */
function useIsDesktop() {
    const [isDesktop, setIsDesktop] = React.useState(
        () => window.matchMedia("(min-width: 768px)").matches,
    );

    React.useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const handler = (e) => setIsDesktop(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    return isDesktop;
}

// ============================================================================
// PRESENTATIONAL COMPONENTS
// ============================================================================

/**
 * Renders inline text with bold formatting and LaTeX support.
 *
 * Markdown links are handled first. A reply can mix our own `table:` syntax
 * with markdown prose — GPT answers do exactly that — and this renderer owns
 * the whole message in that case, so without this the prose kept its
 * `[label](url)` printed verbatim.
 */
function FormattedText({ text }) {
    const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
    if (linkRegex.test(text)) {
        linkRegex.lastIndex = 0;
        const nodes = [];
        let cursor = 0;
        let linkMatch;
        while ((linkMatch = linkRegex.exec(text)) !== null) {
            if (linkMatch.index > cursor) {
                nodes.push(
                    <FormattedText
                        key={`pre-${cursor}`}
                        text={text.slice(cursor, linkMatch.index)}
                    />,
                );
            }
            nodes.push(
                <a
                    key={`link-${linkMatch.index}`}
                    href={linkMatch[2]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-600 dark:text-orange-400 underline underline-offset-2 hover:text-orange-700 dark:hover:text-orange-300 break-words"
                >
                    {linkMatch[1]}
                </a>,
            );
            cursor = linkMatch.index + linkMatch[0].length;
        }
        if (cursor < text.length) {
            nodes.push(
                <FormattedText key={`tail-${cursor}`} text={text.slice(cursor)} />,
            );
        }
        return <>{nodes}</>;
    }

    const boldRegex = /\*\*([^*]+)\*\*/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            const beforeText = text.slice(lastIndex, match.index);
            parts.push(
                <EnhancedLatexRenderer
                    key={`text-${lastIndex}`}
                    text={beforeText}
                />,
            );
        }

        // Add bold text
        parts.push(
            <strong
                key={`bold-${match.index}`}
                className="font-semibold text-orange-600 dark:text-orange-300"
            >
                <EnhancedLatexRenderer text={match[1]} />
            </strong>,
        );

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push(
            <EnhancedLatexRenderer
                key={`text-${lastIndex}`}
                text={text.slice(lastIndex)}
            />,
        );
    }

    // No formatting found
    if (parts.length === 0) {
        return <EnhancedLatexRenderer text={text} />;
    }

    return <>{parts}</>;
}

/**
 * Renders markdown-style content (bullet lists, paragraphs)
 */
function MarkdownRenderer({ text }) {
    const lines = text.split("\n");
    const elements = [];
    let currentList = [];

    const flushList = () => {
        if (currentList.length === 0) return;

        elements.push(
            <ul
                key={`list-${elements.length}`}
                className="list-disc list-inside my-2 space-y-1"
            >
                {currentList.map((item, idx) => (
                    <li key={idx} className="ml-4">
                        <FormattedText text={item} />
                    </li>
                ))}
            </ul>,
        );
        currentList = [];
    };

    lines.forEach((line) => {
        // Check for bullet point (starts with * followed by space)
        if (/^\s*\*\s+/.test(line)) {
            const content = line.replace(/^\s*\*\s+/, "").trim();
            currentList.push(content);
            return;
        }

        // Not a list item - flush pending list
        flushList();

        // Add regular text or spacing
        if (line.trim()) {
            elements.push(
                <div key={`text-${elements.length}`} className="mb-1">
                    <FormattedText text={line} />
                </div>,
            );
        } else if (elements.length > 0) {
            elements.push(
                <div key={`space-${elements.length}`} className="h-2" />,
            );
        }
    });

    // Flush remaining list items
    flushList();

    return <div>{elements}</div>;
}

/**
 * Renders a markdown table with styling
 */
export function TableRenderer({ tableContent }) {
    const lines = tableContent.trim().split("\n");
    if (lines.length < 2) return null;

    // Parse headers
    const headers = parseTableRow(lines[0]);
    if (headers.length === 0) return null;

    // Check for separator line
    const hasSeparator = lines[1] && /^[\s|:-]+$/.test(lines[1].trim());
    const rowStartIndex = hasSeparator ? 2 : 1;

    // Parse data rows, normalising every row to the header count so each row
    // renders the same number of <td>s and the columns stay aligned. Rows that
    // omit trailing empty cells are padded; anything extra is dropped.
    const rows = lines
        .slice(rowStartIndex)
        .map(parseTableRow)
        .filter((row) => row.length > 0)
        .map((row) =>
            Array.from({ length: headers.length }, (_, i) => row[i] ?? ""),
        );

    return (
        <div className="my-4 w-4/5 mx-auto overflow-x-auto">
            <table className="w-full border-collapse border border-orange-500/30 rounded-lg overflow-hidden">
                <thead>
                    <tr className="bg-orange-500/20">
                        {headers.map((header, idx) => (
                            <th
                                key={idx}
                                className="border border-orange-500/30 px-4 py-2 text-left text-black dark:text-white font-semibold"
                                style={getColumnStyle(idx, headers.length)}
                            >
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIdx) => (
                        <tr
                            key={rowIdx}
                            className={
                                rowIdx % 2 === 0 ? "bg-white/5" : "bg-white/10"
                            }
                        >
                            {row.map((cell, cellIdx) => (
                                <td
                                    key={cellIdx}
                                    className="border border-orange-500/20 px-4 py-2 text-black dark:text-white"
                                    style={getColumnStyle(
                                        cellIdx,
                                        headers.length,
                                    )}
                                >
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Parse a table row into cells.
 *
 * Only the leading/trailing empties created by the row's own outer pipes are
 * dropped — interior empties are real, meaningful cells. Filtering out every
 * empty string collapses a row like `| Color | Blue | | |` down to 2 cells, so
 * it no longer lines up with its 4 headers and the column widths (which are
 * derived from cell counts) end up disagreeing between rows.
 */
function parseTableRow(line) {
    const cells = line.split("|").map((cell) => cell.trim());
    if (cells.length && cells[0] === "") cells.shift();
    if (cells.length && cells[cells.length - 1] === "") cells.pop();
    return cells.every((cell) => cell === "") ? [] : cells;
}

/**
 * Get column styling based on index and the table's column count.
 *
 * `totalColumns` must be the header count, not the current row's cell count —
 * a short row would otherwise give its columns a different width than the same
 * columns get in a full row, and the conflicting widths squeeze text into a
 * one-character-per-line column.
 */
function getColumnStyle(index, totalColumns) {
    return {
        wordBreak: "break-word",
        overflowWrap: "break-word",
        whiteSpace: "normal",
        width:
            totalColumns > 1
                ? index === 0
                    ? "20%"
                    : `${80 / (totalColumns - 1)}%`
                : "100%",
    };
}

// ============================================================================
// PDF PANEL CONTEXT — shared state for the side-panel PDF viewer
// ============================================================================

export const PdfPanelContext = React.createContext(null);

/**
 * Wrap the page with this provider so all ActionLinks share one PDF side-panel.
 */
export function PdfPanelProvider({ children }) {
    const [openPdfs, setOpenPdfs] = React.useState([]);

    const toggle = React.useCallback((link) => {
        setOpenPdfs((prev) => {
            if (prev.some((p) => p.url === link.url)) {
                return prev.filter((p) => p.url !== link.url);
            }
            if (prev.length < 2) {
                return [...prev, link];
            }
            // Both slots full → replace the oldest
            return [prev[1], link];
        });
    }, []);

    const closeAll = React.useCallback(() => setOpenPdfs([]), []);

    return (
        <PdfPanelContext.Provider value={{ openPdfs, toggle, closeAll }}>
            {children}
        </PdfPanelContext.Provider>
    );
}

/**
 * Right-side PDF panel — rendered once at the page level.
 * Shows up to 2 PDFs stacked vertically.
 */
export function PdfSidePanel() {
    const ctx = React.useContext(PdfPanelContext);
    if (!ctx || ctx.openPdfs.length === 0) return null;

    const { openPdfs, toggle, closeAll } = ctx;

    return (
        <div className="flex flex-col h-full w-full border-l border-orange-500/30 bg-white dark:bg-gray-900 overflow-hidden">
            {/* Panel top bar */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-orange-500/20 flex-shrink-0">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                    Documents ({openPdfs.length}/2)
                </span>
                <button
                    onClick={closeAll}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    title="Close all"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>

            {/* PDF panels stacked */}
            <div className="flex flex-col flex-1 overflow-hidden divide-y divide-orange-500/20">
                {openPdfs.map((pdf) => (
                    <InlinePdfPanel
                        key={pdf.url}
                        pdf={pdf}
                        isSplit={openPdfs.length === 2}
                        onClose={() => toggle(pdf)}
                        sidePanelMode
                    />
                ))}
            </div>
        </div>
    );
}

/**
 * Renders action links (source documents) — buttons only; PDF opens in side panel
 */
export function ActionLinks({ links }) {
    const ctx = React.useContext(PdfPanelContext);
    const isDesktop = useIsDesktop();

    // Fallback: local state if no provider is present (e.g. chat widget)
    const [localOpenPdfs, setLocalOpenPdfs] = React.useState([]);

    if (!links || links.length === 0) return null;

    const openPdfs = ctx ? ctx.openPdfs : localOpenPdfs;

    const toggle = ctx
        ? ctx.toggle
        : (link) => {
              setLocalOpenPdfs((prev) => {
                  if (prev.some((p) => p.url === link.url))
                      return prev.filter((p) => p.url !== link.url);
                  if (prev.length < 2) return [...prev, link];
                  return [prev[1], link];
              });
          };

    const isOpen = (link) => openPdfs.some((p) => p.url === link.url);

    return (
        <div className="mt-3 py-3 border-t-2 border-orange-500 dark:border-orange-500/20">
            <div className="text-sm text-gray-500 dark:text-gray-400 pb-1">
                more info:
            </div>

            {/* Buttons */}
            <div className="flex flex-row gap-2 flex-wrap">
                {links.map((link, index) => (
                    <ActionLinkButton
                        key={index}
                        link={link}
                        isActive={isOpen(link)}
                        slotIndex={openPdfs.findIndex(
                            (p) => p.url === link.url,
                        )}
                        onToggle={() => toggle(link)}
                        isDesktop={isDesktop}
                    />
                ))}
            </div>

            {/* Inline PDF panels — only rendered when NO side-panel provider is present */}
            {!ctx && localOpenPdfs.length > 0 && (
                <div
                    className={`mt-3 gap-3 ${
                        localOpenPdfs.length === 2
                            ? "flex flex-row"
                            : "flex flex-col"
                    }`}
                >
                    {localOpenPdfs.map((pdf) => (
                        <InlinePdfPanel
                            key={pdf.url}
                            pdf={pdf}
                            isSplit={localOpenPdfs.length === 2}
                            onClose={() => toggle(pdf)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * A single inline PDF panel (header + iframe)
 */
function InlinePdfPanel({ pdf, isSplit, onClose, sidePanelMode = false }) {
    return (
        <div
            className={`overflow-hidden border border-orange-500/30 flex flex-col ${
                sidePanelMode
                    ? "flex-1 min-h-0"
                    : `rounded-xl shadow-lg ${isSplit ? "flex-1 min-w-0" : "w-full"}`
            }`}
        >
            {/* Panel header */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-orange-500/20 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <svg
                        className="w-4 h-4 text-orange-500 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                        {pdf.title || "Document"}
                    </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    <a
                        href={pdf.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in new tab"
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
                    >
                        <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                        </svg>
                        New tab
                    </a>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        title="Close"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* iframe */}
            <div
                className={`relative bg-gray-100 dark:bg-gray-900 ${sidePanelMode ? "flex-1 min-h-0" : ""}`}
                style={
                    sidePanelMode ? {} : { height: isSplit ? "60vh" : "70vh" }
                }
            >
                <iframe
                    key={pdf.url}
                    src={`${pdf.url}#toolbar=1&navpanes=0`}
                    title={pdf.title || "PDF Document"}
                    className="absolute inset-0 w-full h-full border-0"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 text-xs pointer-events-none select-none gap-2">
                    <svg
                        className="w-8 h-8 opacity-20"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    <span className="opacity-30">
                        If PDF does not load, use "New tab"
                    </span>
                </div>
            </div>
        </div>
    );
}

/**
 * Single action link button — on desktop toggles the inline PDF side panel;
 * on mobile (< md) opens the URL directly in a new tab.
 */
function ActionLinkButton({ link, isActive, slotIndex, onToggle, isDesktop }) {
    const baseClass =
        "inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 hover:scale-105 text-sm font-medium";

    // The panel is an iframe. Files we host ourselves load fine; tglobalcorp.com
    // pages send X-Frame-Options, so the panel stays blank while the same link
    // opens normally in a new tab. Only those go straight to a new tab — every
    // other link keeps the side panel.
    if (!isDesktop || isFrameBlocked(link.url)) {
        // Mobile: plain link → new tab
        return (
            <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${baseClass} bg-orange-500/90 text-white border-orange-500/30`}
            >
                <DownloadIcon />
                {link.title || "View Document"}
                <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                </svg>
            </a>
        );
    }

    // Desktop: toggle side panel
    return (
        <button
            type="button"
            onClick={onToggle}
            className={`${baseClass} ${
                isActive
                    ? "bg-orange-600 text-white border-orange-600 shadow-md shadow-orange-500/30"
                    : "bg-orange-500/90 dark:bg-orange-500/20 dark:hover:bg-orange-500/30 text-white dark:text-orange-300 hover:text-orange-200 border-orange-500/30"
            }`}
        >
            <DownloadIcon />
            {link.title || "View Document"}
            {/* Slot badge when open */}
            {isActive && slotIndex !== -1 && (
                <span className="w-4 h-4 rounded-full bg-white/30 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                    {slotIndex + 1}
                </span>
            )}
            <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${isActive ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                />
            </svg>
        </button>
    );
}

/**
 * Download icon SVG
 */
function DownloadIcon() {
    return (
        <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
        </svg>
    );
}

// ============================================================================
// CONTENT BLOCK RENDERER
// ============================================================================

/**
 * Renders a single content block based on its type
 */
function ContentBlockRenderer({ block, language }) {
    switch (block.type) {
        case ContentType.TEXT:
            return (
                <div className="mb-2">
                    <MarkdownRenderer text={block.content} />
                </div>
            );

        case ContentType.TABLE:
            return <TableRenderer tableContent={block.content} />;

        case ContentType.VIZ:
            return (
                <AttributeVisualization
                    vizData={block.content}
                    language={language}
                    className="overflow-x-auto"
                />
            );

        default:
            return null;
    }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Main message renderer component
 * Parses and renders message text with support for:
 * - Markdown formatting (bold, lists)
 * - Tables (table: keyword)
 * - Visualizations (viz: keyword)
 * - Action links (source documents)
 *
 * @param {string} text - The message text to render
 * @param {Array} actionLinks - Optional pre-extracted action links
 * @param {string} language - Language for visualization component
 */
export function MessageRenderer({
    text,
    actionLinks: providedActionLinks,
    language = "en",
    hideActionLinks = false,
}) {
    // Guard against missing/non-string text (e.g. legacy history rows that
    // carry a role but no message). Everything below assumes a string.
    const safeText = typeof text === "string" ? text : "";

    // Step 1: Clean text and extract links
    const cleanedText = cleanMessageText(safeText);
    // Callers can opt out of the source-document buttons (e.g. history page).
    const actionLinks = hideActionLinks
        ? []
        : providedActionLinks || extractActionLinks(safeText);

    // Step 2: Parse text into content blocks
    const contentBlocks = parseContentBlocks(cleanedText);

    // Step 3: Render all blocks
    return (
        <div className="flex flex-col">
            {contentBlocks.map((block, index) => (
                <ContentBlockRenderer
                    key={`block-${index}`}
                    block={block}
                    language={language}
                />
            ))}
            <ActionLinks links={actionLinks} />
        </div>
    );
}
